/**
 * Pure mappers from property form fields onto the nested API Value Objects.
 * Keeps write-path keys aligned with PropertiesModel (ADL-010).
 */

import { parkingTypeWhenSpotsExist } from './property-parking';

type StringList = string[] | undefined;

export type FormAmenityFields = {
  leisure_areas?: StringList;
  facilities?: StringList;
  security?: StringList;
  infrastructure?: StringList;
};

export type FormCharacteristicFields = {
  bedrooms?: number;
  suites?: number;
  bathrooms?: number;
  parking_spots?: number;
  total_area?: number;
  constructed_area?: number;
  floor?: number;
  year_built?: number;
  furnished?: string;
  facing?: string;
  parking_type?: string;
  accepts_pets?: boolean;
  balcony?: boolean;
  gourmet_balcony?: boolean;
  total_rooms?: number;
};

export type FormTaxFields = {
  iptu?: number;
};

const TRANSACTION_LABEL_TO_KEY: Record<string, string> = {
  venda: 'sale',
  aluguel: 'rental',
  permuta: 'exchange',
  sale: 'sale',
  rental: 'rental',
  exchange: 'exchange',
};

/**
 * Returns the list when non-empty, otherwise undefined.
 *
 * @param items - Candidate string list.
 * @returns The list or undefined.
 */
function nonEmptyList(items: StringList): string[] | undefined {
  return items?.length ? items : undefined;
}

/**
 * Coerces a falsy numeric/string value to undefined.
 *
 * @param value - Candidate value.
 * @returns The value when truthy, otherwise undefined.
 */
function orUndef<T>(value: T): T | undefined {
  return value || undefined;
}

/**
 * Keeps a picked count, 0 included: the count selectors open with nothing
 * picked, so a 0 is the broker's answer (a studio has no bedroom).
 *
 * @param value - The form count.
 * @returns The count, or undefined when nothing was picked.
 */
function countOrUndef(value: number | undefined): number | undefined {
  return value == null || Number.isNaN(value) ? undefined : value;
}

/**
 * Maps form amenity lists onto canonical `PropertyAmenities` keys.
 *
 * @param fields - Form leisure/facilities/security lists.
 * @returns Nested amenities object (may be empty).
 */
export function mapFormAmenitiesToNested(fields: FormAmenityFields): {
  external?: string[];
  internal?: string[];
  security?: string[];
  infrastructure?: string[];
} {
  return {
    external: nonEmptyList(fields.leisure_areas),
    internal: nonEmptyList(fields.facilities),
    security: nonEmptyList(fields.security),
    infrastructure: nonEmptyList(fields.infrastructure),
  };
}

/**
 * Maps form characteristic fields onto nested `PropertyCharacteristics`.
 * Parking type is dropped when there are no parking spots.
 *
 * @param fields - Form characteristic inputs.
 * @returns Nested characteristics object.
 */
export function mapFormCharacteristicsToNested(
  fields: FormCharacteristicFields,
): Record<string, unknown> {
  return {
    bedrooms: countOrUndef(fields.bedrooms),
    suites: countOrUndef(fields.suites),
    bathrooms: countOrUndef(fields.bathrooms),
    parking_spots: countOrUndef(fields.parking_spots),
    total_area: orUndef(fields.total_area),
    constructed_area: orUndef(fields.constructed_area),
    floor_number: orUndef(fields.floor),
    year_built: orUndef(fields.year_built),
    furnished: orUndef(fields.furnished),
    facing: orUndef(fields.facing),
    parking_type: parkingTypeWhenSpotsExist(fields.parking_spots, fields.parking_type),
    accepts_pets:
      typeof fields.accepts_pets === 'boolean' ? fields.accepts_pets : undefined,
    balcony:
      typeof fields.balcony === 'boolean' ? fields.balcony : undefined,
    gourmet_balcony:
      typeof fields.gourmet_balcony === 'boolean' ? fields.gourmet_balcony : undefined,
    total_rooms: orUndef(fields.total_rooms),
  };
}

/**
 * Maps form IPTU onto nested `TaxData`.
 *
 * @param fields - Form tax inputs.
 * @returns Taxes block, or undefined when empty.
 */
export function mapFormPricingTaxes(
  fields: FormTaxFields,
): { iptu_annual: number } | undefined {
  if (!fields.iptu) return undefined;
  return { iptu_annual: fields.iptu };
}

/**
 * Normalizes transaction type labels (pt-BR or EN) to detail-view keys.
 *
 * @param types - Raw transaction type strings from the API/form.
 * @returns Deduped display keys: sale | rental | exchange.
 */
export function normalizeTransactionTypeKeys(types: string[] | undefined): string[] {
  if (!types?.length) return [];
  const keys: string[] = [];
  for (const raw of types) {
    const mapped = TRANSACTION_LABEL_TO_KEY[raw.trim().toLowerCase()];
    if (mapped && !keys.includes(mapped)) keys.push(mapped);
  }
  return keys;
}
